import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useFleetApi, type OverviewSummary } from "./api";

function fmtAgo(iso: string | null): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface TileProps {
  label: string;
  value: number;
  sub?: string;
  tone?: "ok" | "warn" | "crit";
  href?: string;
}

function StatTile({ label, value, sub, tone, href }: TileProps) {
  const toneColor =
    tone === "crit" ? "var(--crit)" : tone === "warn" ? "var(--warn)" : "var(--text)";
  const inner = (
    <div
      className="card"
      style={{
        padding: "14px 16px",
        cursor: href ? "pointer" : "default",
        minHeight: "78px",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--text-3)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "26px",
          fontWeight: 600,
          color: toneColor,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.15,
          marginTop: "4px",
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: "11.5px", color: "var(--text-2)", marginTop: "2px" }}>{sub}</div>
      )}
    </div>
  );
  return href ? (
    <Link to={href} style={{ textDecoration: "none", color: "inherit" }}>
      {inner}
    </Link>
  ) : (
    inner
  );
}

export default function Overview() {
  const { token, logout } = useAuth();
  const { fetchOverview } = useFleetApi(token, logout);
  const [data, setData] = useState<OverviewSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchOverview()
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [fetchOverview]);

  if (err) {
    return (
      <section>
        <p style={{ color: "var(--crit)" }}>{err}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section>
        <p className="muted">Loading overview…</p>
      </section>
    );
  }

  const taskTotal = Object.values(data.tasks.by_status).reduce((a, b) => a + b, 0);
  const failedShare =
    taskTotal > 0 ? Math.round(((data.tasks.by_status.failed ?? 0) / taskTotal) * 100) : 0;

  return (
    <section>
      <p className="page-lede">
        Health of the fleet at a glance. Last event {fmtAgo(data.audit.last_event_at)}.
        Click any tile to drill in.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "10px",
          marginBottom: "18px",
        }}
      >
        <StatTile
          label="Open escalations"
          value={data.escalations.open}
          tone={data.escalations.open > 0 ? "warn" : "ok"}
          href="/fleet/escalations"
        />
        <StatTile
          label="Actions (24h)"
          value={data.actions.last_24h}
          sub={data.actions.reverted > 0 ? `${data.actions.reverted} reverted` : undefined}
          tone={data.actions.reverted > 0 ? "warn" : "ok"}
          href="/fleet/activity"
        />
        <StatTile
          label="Audit events (24h)"
          value={data.audit.last_24h}
          sub={`${data.audit.distinct_agents} agents`}
          href="/fleet/activity"
        />
        <StatTile
          label="Tasks failing (24h)"
          value={data.tasks.failed_last_24h}
          sub={failedShare > 0 ? `${failedShare}% of all tasks` : undefined}
          tone={data.tasks.failed_last_24h > 0 ? "crit" : "ok"}
          href="/fleet/tasks?status=failed"
        />
      </div>

      <div className="card" style={{ marginBottom: "12px" }}>
        <h3>Tasks by status</h3>
        {Object.keys(data.tasks.by_status).length === 0 ? (
          <p className="muted">No tasks yet.</p>
        ) : (
          <div className="chips" style={{ marginTop: "6px" }}>
            {Object.entries(data.tasks.by_status).map(([s, n]) => (
              <Link
                key={s}
                to={`/fleet/tasks?status=${encodeURIComponent(s)}`}
                className={`chip${s === "failed" ? " active" : ""}`}
                style={{ textDecoration: "none" }}
              >
                {s}: <strong>{n}</strong>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h3>Top agents (7d)</h3>
        {data.top_agents_7d.length === 0 ? (
          <p className="muted">No agent activity in the last 7 days.</p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: "4px",
              maxWidth: "420px",
            }}
          >
            {data.top_agents_7d.map(a => (
              <Fragment key={a.agent}>
                <Link to={`/fleet/activity?agent=${encodeURIComponent(a.agent)}`}>
                  {a.agent.replace(/_/g, " ")}
                </Link>
                <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--text-2)" }}>
                  {a.events_7d}
                </span>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
